package er.prateek.crossly.ui.screens.riddle

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.key.utf16CodePoint
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.LifecycleEventObserver
import androidx.lifecycle.compose.LocalLifecycleOwner
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.riddle.Riddle
import er.prateek.crossly.data.model.riddle.RiddleSegment
import er.prateek.crossly.data.model.riddle.ShapeToken
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.progression.recordCompletionAndLog
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.game.riddle.RiddleRunEngine
import er.prateek.crossly.generation.riddle.RiddleGenerator
import er.prateek.crossly.ui.coach.reportCoachBounds
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.IconBell
import er.prateek.crossly.ui.components.IconBulb
import er.prateek.crossly.ui.components.IconCircle
import er.prateek.crossly.ui.components.IconDiamond
import er.prateek.crossly.ui.components.IconFlame
import er.prateek.crossly.ui.components.IconHeart
import er.prateek.crossly.ui.components.IconSquare
import er.prateek.crossly.ui.components.IconStar
import er.prateek.crossly.ui.components.IconTriangle
import er.prateek.crossly.ui.components.IconTrophy
import er.prateek.crossly.ui.components.NumberPad
import er.prateek.crossly.ui.components.StatChip
import er.prateek.crossly.ui.screens.math.ModeCoachStep
import er.prateek.crossly.ui.screens.math.ModeCoachTour
import er.prateek.crossly.ui.screens.math.tierLabel
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.window
import kotlin.time.TimeSource
import kotlin.random.Random
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private val MODE = GameMode.RIDDLES.analyticsName

/** What a finished run hands back: enough to score it, show it, and share it as a challenge. */
data class RiddleRunResult(
    val levelId: String,
    /** `0` means the run fell short of the pass mark and nothing was recorded. */
    val stars: Int,
    val elapsedMs: Long,
    /** The seed the questions were generated from; replaying it gives the identical set. */
    val seed: Long,
    val correct: Int,
    val total: Int,
    /** Correct answers this run needed to clear the set. */
    val passMark: Int,
)

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun RiddleRunScreen(
    levelId: String,
    isDaily: Boolean = false,
    /** `0` rolls a fresh question seed; a challenge passes its friend's seed so both play the same set. */
    seed: Long = 0L,
    /** Set when racing a friend's challenge. */
    challengerName: String? = null,
    onBack: () -> Unit,
    onFinished: (RiddleRunResult) -> Unit,
) {
    val tier = remember(levelId) {
        runCatching { DifficultyTier.valueOf(levelId.split("-")[1].uppercase()) }.getOrDefault(DifficultyTier.EASY)
    }
    val index = remember(levelId) { levelId.substringAfterLast("-").toIntOrNull() ?: 1 }
    val config = AppContainer.appConfig().modes.riddles

    val runSeed = remember(levelId, seed) { if (seed != 0L) seed else Random.nextLong(1L, 1_000_000L) }
    val set = remember(levelId, runSeed) {
        val cfg = AppContainer.appConfig().modes.riddles
        RiddleGenerator.generateSet(
            levelId = levelId,
            tier = tier,
            count = cfg.questionsPerLevel,
            nonce = runSeed,
            options = RiddleGenerator.Options(
                allowArithmetic = cfg.allowArithmetic,
                allowSequences = cfg.allowSequences,
                allowShapeAlgebra = cfg.allowShapeAlgebra,
                allowMultiply = cfg.allowMultiply,
                allowDivide = cfg.allowDivide,
                allowLogic = cfg.allowLogic,
            ),
            levelIndex = index,
            levelsPerTier = cfg.levelsPerTier,
        )
    }
    val engine = remember(set) { RiddleRunEngine(set, passRatio = AppContainer.appConfig().modes.riddles.passRatio) }
    val colors = MaterialTheme.game

    val perQuestionMs = config.perQuestionSeconds * 1000L

    LaunchedEffect(set) {
        // A Daily that rolled a riddle set is still a daily: same event pair as the word Daily.
        AppContainer.analytics.logEvent(
            if (isDaily) AnalyticsEvents.DAILY_PUZZLE_STARTED else AnalyticsEvents.PUZZLE_STARTED,
            mapOf(
                "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "level_index" to index,
                "question_count" to engine.total, "from_challenge" to (challengerName != null),
            ),
        )
    }

    // First-run tour: both clocks wait for it to finish.
    var coachVisible by remember(set) { mutableStateOf(!AppContainer.coachPrefsStore.hasSeenRiddleCoach()) }
    LaunchedEffect(set) {
        if (coachVisible) {
            AppContainer.analytics.logEvent(AnalyticsEvents.PLAY_COACH_SHOWN, mapOf("mode" to MODE, "level_id" to levelId))
        }
    }
    var questionBounds by remember { mutableStateOf(Rect.Zero) }
    var padBounds by remember { mutableStateOf(Rect.Zero) }

    // Both clocks stop while the app is backgrounded (a call, a notification tap), as on the
    // crossword -- otherwise questions would time out while the player can't see them.
    var paused by remember { mutableStateOf(false) }
    val lifecycleOwner = LocalLifecycleOwner.current
    DisposableEffect(lifecycleOwner) {
        val observer = LifecycleEventObserver { _, event ->
            when (event) {
                Lifecycle.Event.ON_PAUSE, Lifecycle.Event.ON_STOP -> paused = true
                Lifecycle.Event.ON_RESUME -> paused = false
                else -> Unit
            }
        }
        lifecycleOwner.lifecycle.addObserver(observer)
        onDispose { lifecycleOwner.lifecycle.removeObserver(observer) }
    }

    var totalElapsedMs by remember(set) { mutableStateOf(0L) }
    LaunchedEffect(set, coachVisible, paused) {
        if (coachVisible || paused) return@LaunchedEffect
        val base = totalElapsedMs
        val mark = TimeSource.Monotonic.markNow()
        while (!engine.isComplete) {
            totalElapsedMs = base + mark.elapsedNow().inWholeMilliseconds
            delay(200)
        }
    }

    // A wrong answer keeps the question, so one question can log several RIDDLE_ANSWERED events.
    // `attempt` (1-based) lets a report keep first tries only, or the final outcome per question.
    var attempt by remember(engine.index) { mutableStateOf(0) }

    fun logAnswered(riddle: Riddle, questionIndex: Int, correct: Boolean, timedOut: Boolean, qElapsedMs: Long) {
        attempt++
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RIDDLE_ANSWERED,
            mapOf(
                "mode" to MODE, "level_id" to levelId, "question_index" to questionIndex, "attempt" to attempt,
                "kind" to riddle.kind.name, "correct" to correct, "timed_out" to timedOut, "elapsed_ms" to qElapsedMs,
            ),
        )
    }

    // Per-question countdown; a timeout is a logged miss and advances. Resumes from what was left.
    var remainingMs by remember(engine.index) { mutableStateOf(perQuestionMs) }
    LaunchedEffect(engine.index, engine.isComplete, coachVisible, paused) {
        if (engine.isComplete || coachVisible || paused) return@LaunchedEffect
        val startLeft = remainingMs
        val mark = TimeSource.Monotonic.markNow()
        while (!engine.isComplete) {
            val left = startLeft - mark.elapsedNow().inWholeMilliseconds
            remainingMs = left.coerceAtLeast(0)
            if (left <= 0) {
                val riddle = engine.current
                val qIndex = engine.index
                engine.timeout()
                riddle?.let { logAnswered(it, qIndex, correct = false, timedOut = true, qElapsedMs = perQuestionMs) }
                break
            }
            delay(100)
        }
    }

    LaunchedEffect(engine.isComplete) {
        if (engine.isComplete) {
            val progression = AppContainer.riddleProgressionManager
            val hadStarsBefore = progression.starsFor(levelId) > 0
            val nextTier = DifficultyTier.entries.getOrNull(tier.ordinal + 1)
            val tierUnlockedBefore = nextTier?.let { progression.isTierUnlocked(it) } ?: false

            // Below the pass mark the run is a loss: 0 stars, and nothing below is recorded --
            // no unlock, no Daily, no streak or badge credit.
            val passed = engine.passed
            val stars = if (passed) {
                progression.recordCompletion(levelId, hintsUsed = 0, mistakes = engine.mistakes, elapsedMs = totalElapsedMs)
            } else {
                0
            }

            if (!hadStarsBefore && stars >= 1) {
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.LEVEL_UNLOCKED,
                    mapOf("mode" to MODE, "level_id" to levelId, "tier" to tier.name, "stars_earned" to stars),
                )
            }
            if (nextTier != null && !tierUnlockedBefore && progression.isTierUnlocked(nextTier)) {
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.TIER_UNLOCKED,
                    mapOf("mode" to MODE, "tier" to nextTier.name, "unlocked_from_tier" to tier.name),
                )
            }
            // "Completed" means cleared, as for the other modes; a run below the pass mark is its own
            // event so it never inflates completion counts.
            AppContainer.analytics.logEvent(
                when {
                    !passed -> AnalyticsEvents.PUZZLE_FAILED
                    isDaily -> AnalyticsEvents.DAILY_PUZZLE_COMPLETED
                    else -> AnalyticsEvents.PUZZLE_COMPLETED
                },
                mapOf(
                    "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "stars" to stars,
                    "mistakes" to engine.mistakes, "correct" to engine.correctCount, "total" to engine.total,
                    "pass_mark" to engine.passMark, "duration_ms" to totalElapsedMs,
                    "is_daily" to isDaily, "from_challenge" to (challengerName != null),
                ),
            )
            if (passed) {
                if (isDaily) AppContainer.dailyPuzzleManager.recordCompletionAndLog(AppContainer.analytics, GameMode.RIDDLES)
                AppContainer.playerJourney.onGameCompleted(GameMode.RIDDLES, isDaily = isDaily)
            }
            onFinished(
                RiddleRunResult(
                    levelId = levelId,
                    stars = stars,
                    elapsedMs = totalElapsedMs,
                    seed = runSeed,
                    correct = engine.correctCount,
                    total = engine.total,
                    passMark = engine.passMark,
                ),
            )
        }
    }

    fun leave() {
        if (!engine.isComplete) {
            AppContainer.analytics.logEvent(
                AnalyticsEvents.PUZZLE_ABANDONED,
                mapOf(
                    "mode" to MODE, "level_id" to levelId, "tier" to tier.name, "answered" to engine.index,
                    "correct" to engine.correctCount, "total" to engine.total, "elapsed_ms" to totalElapsedMs,
                    "is_daily" to isDaily, "from_challenge" to (challengerName != null),
                ),
            )
        }
        onBack()
    }

    fun submitAnswer() {
        val riddle = engine.current ?: return
        val qIndex = engine.index
        val qElapsed = (perQuestionMs - remainingMs).coerceAtLeast(0)
        val correct = engine.submit()
        logAnswered(riddle, qIndex, correct = correct, timedOut = false, qElapsedMs = qElapsed)
    }

    // Shared by the on-screen pad and the keyboard.
    fun typeDigit(ch: Char) {
        engine.appendDigit(ch)
        // Auto-submit the moment the entry is right; wrong answers wait for the submit key.
        if (engine.isEntryCorrect) submitAnswer()
    }

    // Keys only reach the screen while it holds focus. Claim it once the tour is gone and again on
    // every new question, so typing works without first clicking the board.
    val focusRequester = remember { FocusRequester() }
    LaunchedEffect(coachVisible, engine.index) {
        if (!coachVisible) runCatching { focusRequester.requestFocus() }
    }

    // A wrong submit: buzz, shake the card and pop a (varying) themed toast. The question stays put.
    val haptics = LocalHapticFeedback.current
    val shake = remember { Animatable(0f) }
    var toast by remember { mutableStateOf<String?>(null) }
    // A new question starts clean: the last one's "wrong" toast and red border don't carry over.
    LaunchedEffect(engine.index) { toast = null }
    LaunchedEffect(engine.wrongTick) {
        // Typed fast, a miss and the next right answer can land in one frame; by the time this runs
        // the question it was about is gone, so don't flash "wrong" over the fresh one.
        if (engine.wrongTick == 0 || engine.lastWrongIndex != engine.index) return@LaunchedEffect
        haptics.performHapticFeedback(HapticFeedbackType.LongPress)
        toast = WrongToasts.next()
        launch {
            for (target in listOf(-14f, 12f, -9f, 6f, -3f, 0f)) {
                shake.animateTo(target, tween(durationMillis = 45))
            }
        }
        delay(1600)
        toast = null
    }

    val finishCoach: (skipped: Boolean, stepIndex: Int) -> Unit = { skipped, stepIndex ->
        AppContainer.coachPrefsStore.markRiddleCoachSeen()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.PLAY_COACH_FINISHED,
            mapOf("mode" to MODE, "skipped" to skipped, "step" to stepIndex),
        )
        coachVisible = false
    }

    BackHandler(enabled = true) { if (coachVisible) finishCoach(true, 0) else leave() }

    GameScreen(
        adPlacement = AdPlacement.RIDDLE_RUN,
        overlay = {
            if (coachVisible) {
                ModeCoachTour(
                    steps = listOf(
                        ModeCoachStep(
                            title = "Beat the clock",
                            body = "Each question has a timer. Work out the answer before it runs out. A wrong answer holds the question until you get it right.",
                            hole = questionBounds,
                        ),
                        ModeCoachStep(
                            title = "Type your answer",
                            body = "Tap the digits or type them on your keyboard. It submits itself the moment you're right. Use the check key (or Enter) to submit a guess.",
                            hole = padBounds,
                        ),
                    ),
                    onFinish = finishCoach,
                )
            }
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .focusRequester(focusRequester)
                .focusable()
                // Physical keyboard (desktop, web): digits, Backspace/Delete, Enter to submit a guess.
                .onPreviewKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown || coachVisible || engine.isComplete) return@onPreviewKeyEvent false
                    val ch = event.utf16CodePoint.toChar()
                    when {
                        ch.isDigit() -> { typeDigit(ch); true }
                        event.key == Key.Backspace || event.key == Key.Delete -> { engine.backspace(); true }
                        event.key == Key.Enter || event.key == Key.NumPadEnter -> {
                            if (engine.entry.isNotEmpty()) submitAnswer()
                            true
                        }
                        else -> false
                    }
                },
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            GameTopBar(
                title = "Set $index",
                subtitle = if (challengerName != null) "vs $challengerName" else tierLabel(tier),
                onBack = { leave() },
                trailing = {
                    StatChip(
                        value = "${(engine.index + 1).coerceAtMost(engine.total)}/${engine.total}",
                        tint = colors.accent,
                    )
                    val seconds = (remainingMs + 999) / 1000
                    StatChip(
                        value = seconds.toString(),
                        label = "s",
                        tint = if (remainingMs <= 5000) colors.danger else colors.gold,
                    )
                },
            )

            val current = engine.current
            Box(
                modifier = Modifier.weight(1f).fillMaxWidth().reportCoachBounds { questionBounds = it },
                contentAlignment = Alignment.Center,
            ) {
                if (current != null) {
                    GameCard(modifier = Modifier.fillMaxWidth().graphicsLayer { translationX = shake.value.dp.toPx() }) {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(vertical = Spacing.md),
                            verticalArrangement = Arrangement.spacedBy(Spacing.md),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            current.prompt.forEach { line -> PromptLine(line = line) }
                            AnswerBox(entry = engine.entry, wrong = toast != null)
                        }
                    }
                }
            }

            WrongToast(message = toast)

            NumberPad(
                onDigit = { typeDigit(it) },
                onBackspace = { engine.backspace() },
                onSubmit = { submitAnswer() },
                submitEnabled = engine.entry.isNotEmpty(),
                // A short window (a laptop browser under its toolbars) can't fit full-size keys and
                // a three-line question; slimmer keys keep the answer box from being crushed.
                keyHeight = if (MaterialTheme.window.height < 640.dp) 40.dp else 52.dp,
                modifier = Modifier.widthIn(max = 420.dp).align(Alignment.CenterHorizontally)
                    .reportCoachBounds { padBounds = it },
            )
        }
    }
}

/**
 * Gen-Z flavoured "nope" lines; never repeats the previous one back to back. On web the emoji come
 * from the bundled fallback font, so after adding a new one, re-run tools/webfonts/build_webfonts.py.
 */
private object WrongToasts {
    private val lines = listOf(
        "Wrong! 💀 Try again",
        "Bestie, that ain't it 😭",
        "Not quite, no cap 🧢",
        "Math is not mathing 🤨",
        "Cooked 🍳 — go again",
        "That's sus 👀 retry",
        "L + ratio 🫠 one more try",
        "Nah fam, try again ✋",
        "Skill issue? 😅 Again!",
        "Almost... but no 🙃",
        "Brain buffering 🔄 retry",
        "Plot twist: wrong 🎭",
    )
    private var last = -1

    fun next(): String {
        var i = Random.nextInt(lines.size)
        if (i == last) i = (i + 1) % lines.size
        last = i
        return lines[i]
    }
}

@Composable
private fun WrongToast(message: String?) {
    val colors = MaterialTheme.game
    // Fixed-height slot so the toast appearing never shifts the layout.
    Box(modifier = Modifier.fillMaxWidth().heightIn(min = 44.dp), contentAlignment = Alignment.Center) {
        AnimatedVisibility(visible = message != null, enter = fadeIn() + scaleIn(initialScale = 0.8f), exit = fadeOut()) {
            Text(
                text = message.orEmpty(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                textAlign = TextAlign.Center,
                modifier = Modifier
                    .clip(RoundedCornerShape(50))
                    .background(colors.danger)
                    .padding(horizontal = Spacing.lg, vertical = Spacing.sm),
            )
        }
    }
}

@Composable
private fun PromptLine(line: List<RiddleSegment>) {
    val colors = MaterialTheme.game
    Row(
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        line.forEach { segment ->
            when (segment) {
                is RiddleSegment.Text -> Text(
                    text = segment.value,
                    textAlign = TextAlign.Center,
                    style = if (segment.value.length > 26) MaterialTheme.typography.titleLarge else MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = colors.ink,
                )
                is RiddleSegment.Shape -> when (segment.token) {
                    ShapeToken.SQUARE -> IconSquare(size = 28.dp, tint = colors.accent)
                    ShapeToken.CIRCLE -> IconCircle(size = 28.dp, tint = colors.accentAlt)
                    ShapeToken.TRIANGLE -> IconTriangle(size = 28.dp, tint = colors.gold)
                    ShapeToken.STAR -> IconStar(size = 28.dp, tint = colors.gold)
                    ShapeToken.HEART -> IconHeart(size = 28.dp, tint = colors.danger)
                    ShapeToken.FLAME -> IconFlame(size = 28.dp, tint = colors.flame)
                    ShapeToken.BELL -> IconBell(size = 28.dp, tint = colors.accent)
                    ShapeToken.BULB -> IconBulb(size = 28.dp, tint = colors.success)
                    ShapeToken.TROPHY -> IconTrophy(size = 28.dp, tint = colors.accentAlt)
                    // An operator inside a line of digits, so a touch smaller than the variables.
                    ShapeToken.DIAMOND -> IconDiamond(size = 22.dp, tint = colors.accentAlt)
                }
            }
        }
    }
}

@Composable
private fun AnswerBox(entry: String, wrong: Boolean) {
    val colors = MaterialTheme.game
    val borderColor = if (wrong) colors.danger else colors.accent
    Box(
        modifier = Modifier
            .widthIn(min = 96.dp)
            .heightIn(min = 56.dp)
            .clip(RoundedCornerShape(16.dp))
            .background(colors.cellFill)
            .border(2.dp, borderColor, RoundedCornerShape(16.dp))
            .padding(horizontal = Spacing.lg, vertical = Spacing.sm),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = entry.ifEmpty { "?" },
            style = MaterialTheme.typography.headlineLarge,
            fontWeight = FontWeight.Bold,
            color = when {
                wrong -> colors.danger
                entry.isEmpty() -> colors.inkMuted
                else -> colors.cellText
            },
        )
    }
}
